import { useI18NextContext } from '@components/hooks';
import styled from '@emotion/styled';
import { Place } from '@model/iceberg/geography';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Icon } from '@components/common/generic-icon';
import { Theme, ThemeProps } from '@theme/base';
import { StarRating } from '@components/common/star-rating';
import { mq } from '@styles/breakpoints';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { Image } from '@components/common/image';

/* ***************** *
 *       Types       *
 * ***************** */
export interface ProductDescriptionProps {
  place?: Place;
  theme?: Theme;
  testId?: string;
  hotelRating?: number;
  tripAdvisorRating?: string;
  isGhosting?: boolean;
  isMultiCentre?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const HotelTitle = styled.h1(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h2 as any),
  marginBottom: 10,
  fontWeight: 'normal'
}));

const MultiCentreTitle = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h3 as any),
  marginBottom: 10,
  fontWeight: 'normal'
}));

const RatingContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  paddingBottom: theme.custom.spacing.medium,
  marginBottom: theme.custom.spacing.medium
}));

const HotelRating = styled.div(({ theme }: ThemeProps) => ({
  marginRight: theme.custom.spacing.xLarge
}));

const Rating = styled.div(({ theme }: ThemeProps) => ({
  paddingRight: theme.custom.spacing.medium,
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-start'
}));

const PlaceContainer = styled.div(({ theme }: ThemeProps) => ({
  paddingRight: theme.custom.spacing.medium,
  height: '100%',
  [mq.small]: {
    display: 'flex',
    justifyContent: 'flex-start'
  }
}));

const PlaceLabel = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.group4.light,
  marginTop: 10,
  marginRight: theme.custom.spacing.xLarge
}));

const placeLabelIconStyles = (theme: Theme) => ({
  fontSize: '1.5rem',
  color: theme.custom.colors.group4.light,
  marginRight: theme.custom.spacing.xSmall
});

const TripAdvisorRatinImg: any = styled(Image)({});

/* ********************************* *
 *    ProductDescriptionComponent    *
 * ********************************* */

export const ProductDescriptionComponent = ({
  testId,
  theme,
  place,
  hotelRating,
  tripAdvisorRating,
  isGhosting,
  isMultiCentre
}: ProductDescriptionProps) => {
  const t: any = useI18NextContext();

  if (isGhosting || !place) {
    return (
      <Container>
        <SkeletonElement variant={SkeletonVariant.HEADING} />
        <RatingContainer>
          <Rating>
            <SkeletonElement variant={SkeletonVariant.TEXT} repeat={2} width={'10%'} inline />
          </Rating>
          <PlaceContainer>
            <SkeletonElement variant={SkeletonVariant.TEXT} repeat={2} width={'20%'} inline />
          </PlaceContainer>
        </RatingContainer>
      </Container>
    );
  }

  return (
    <Container data-testid={testId || TestId.productDescription.main}>
      {isMultiCentre ? (
        <MultiCentreTitle data-testid={TestId.productDescription.title}>{place.hotel!.display}</MultiCentreTitle>
      ) : (
        <HotelTitle data-testid={TestId.productDescription.title}>{place.hotel!.display}</HotelTitle>
      )}

      <RatingContainer>
        <Rating>
          <HotelRating>
            <StarRating rating={hotelRating} size={20} data-testid={TestId.productDescription.starRating} />
          </HotelRating>
          {tripAdvisorRating && (
            <TripAdvisorRatinImg
              data-testid={TestId.productDescription.tripAdvisorRating}
              src={tripAdvisorRating}
              alt={t('alt__product-description--tripadvisor')}
              width={120}
              height={25}
            />
          )}
        </Rating>
        <PlaceContainer>
          <PlaceLabel data-testid={TestId.productDescription.placeLabel}>
            <Icon name={'mhi-map-pin-outline'} css={placeLabelIconStyles(theme!)} />
            {place.country.name.display}, {place.continent.display}
          </PlaceLabel>
          <PlaceLabel data-testid={TestId.productDescription.resortLabel}>
            <Icon name={'mhi-holidays'} css={placeLabelIconStyles(theme!)} />
            {place.resort.display}
          </PlaceLabel>
        </PlaceContainer>
      </RatingContainer>
    </Container>
  );
};

export const ProductDescription = withTheme(ProductDescriptionComponent);
